/**
 * CustomPromptAction Component
 * Action button for custom AI prompts
 * Opens a modal instead of dropdown
 * Shows loading state while processing
 */
import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import { getWorkspaceColors } from '../styles';
import { ACTION_TYPES, ACTIONS_CONFIG } from '../constants';
import CustomPromptModal from './CustomPromptModal';

const CustomPromptAction = ({ 
  onExecute,
  isProcessing = false,
}) => {
  const { colors, theme } = useTheme();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  const config = ACTIONS_CONFIG[ACTION_TYPES.CUSTOM];

  const handleClick = () => {
    if (isProcessing) return;
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = async (prompt) => {
    setIsModalOpen(false);
    await onExecute?.(prompt);
  };

  const chipStyles = {
    button: {
      padding: '10px 18px',
      borderRadius: 24,
      border: 'none',
      backgroundColor: isProcessing 
        ? workspaceColors.bgSecondary
        : (workspaceColors.chipBg || workspaceColors.bgTertiary),
      color: isProcessing 
        ? workspaceColors.textSecondary
        : workspaceColors.textPrimary,
      cursor: isProcessing ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 14,
      fontWeight: 600,
      transition: 'all 0.15s ease',
    },
    spinner: {
      animation: 'customPromptSpin 1s linear infinite',
    },
  };

  return (
    <>
      <button
        style={chipStyles.button}
        onClick={handleClick}
        disabled={isProcessing}
        onMouseEnter={(e) => {
          if (!isProcessing) {
            e.currentTarget.style.backgroundColor = workspaceColors.chipHover || workspaceColors.bgSecondary;
          }
        }}
        onMouseLeave={(e) => {
          if (!isProcessing) {
            e.currentTarget.style.backgroundColor = workspaceColors.chipBg || workspaceColors.bgTertiary;
          }
        }}
      >
        {isProcessing ? (
          <>
            <Loader2 size={18} style={chipStyles.spinner} />
            Gerando...
          </>
        ) : (
          <>
            <Sparkles size={18} />
            {config.label}
          </>
        )}
      </button>

      <CustomPromptModal
        isOpen={isModalOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        isProcessing={isProcessing}
      />

      <style>{`
        @keyframes customPromptSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default CustomPromptAction;